import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faCalendarAlt,
  faCheckCircle,
  faClock,
  faGlobe,
  faInfoCircle,
  faLayerGroup,
  faLock,
  faPlayCircle,
} from '@fortawesome/free-solid-svg-icons';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { getConfig } from '@edx/frontend-platform';
import { ErrorPage } from '@edx/frontend-platform/react';
import { useIntl } from '@edx/frontend-platform/i18n';

import noCourseImg from '@src/assets/images/no-course-image.svg';
import { AlertNotification, Head, Loading } from '@src/generic';
import { getStartDateDisplay } from '@src/generic/course-card/utils';
import { baseAppUrl, formatDate } from '@src/utils';
import { ROUTES } from '@src/routes';
import { useCourseAboutData } from './data/hooks';
import { useEnrollmentActions } from './course-intro/hooks/useEnrollmentActions';
import { getLearningHomePageUrl } from './course-intro/utils';
import { processOverviewContent } from './course-overview/utils';
import messages from './messages';

type PrimaryAction =
  | { kind: 'link'; label: string; href: string; status: string; helper?: string }
  | { kind: 'button'; label: string; onClick: () => void; status: string; helper?: string; disabled?: boolean }
  | { kind: 'none'; status: string; helper: string };

type DetailItem = {
  icon: typeof faLayerGroup;
  label: string;
  value: string;
};

const getLanguageName = (value: string, locale: string) => {
  try {
    const languageNames = new Intl.DisplayNames([locale, 'fr'], { type: 'language' });
    return languageNames.of(value) || value.toUpperCase();
  } catch (error) {
    return value.toUpperCase();
  }
};

const getPacingLabel = (intl: ReturnType<typeof useIntl>, pacing?: string) => {
  if (pacing === 'instructor') {
    return intl.formatMessage(messages.pacingInstructor);
  }

  if (pacing === 'self') {
    return intl.formatMessage(messages.pacingSelf);
  }

  return intl.formatMessage(messages.pacingFlexible);
};

const getPriceLabel = (intl: ReturnType<typeof useIntl>, price?: string, isPaid?: boolean) => {
  if (!price || /^free$/i.test(price)) {
    return isPaid ? intl.formatMessage(messages.pricePaid) : intl.formatMessage(messages.priceFree);
  }

  return price;
};

const resolveCourseImage = (image?: string | null) => {
  if (!image) {
    return noCourseImg;
  }

  return baseAppUrl(image) || noCourseImg;
};

const normalizeTextList = (value?: string | string[]) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  return value
    .split(/\r?\n|•|;|,/)
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const renderPrimaryAction = (primaryAction: PrimaryAction) => {
  if (primaryAction.kind === 'link') {
    return (
      <a className="catalog-ui-button catalog-ui-button--primary" href={primaryAction.href}>
        {primaryAction.label}
        <FontAwesomeIcon icon={faArrowRight} />
      </a>
    );
  }

  if (primaryAction.kind === 'button') {
    return (
      <button
        type="button"
        className="catalog-ui-button catalog-ui-button--primary"
        onClick={primaryAction.onClick}
        disabled={primaryAction.disabled}
      >
        {primaryAction.label}
        {!primaryAction.disabled ? <FontAwesomeIcon icon={faArrowRight} /> : null}
      </button>
    );
  }

  return null;
};

const CourseAboutPage = () => {
  const intl = useIntl();
  const { courseId = '' } = useParams();
  const authenticatedUser = getAuthenticatedUser();
  const { LMS_BASE_URL, INFO_EMAIL } = getConfig();
  const {
    data: courseAboutData,
    isLoading,
    isError,
  } = useCourseAboutData(courseId);

  const {
    enrollmentError,
    isEnrollmentPending,
    handleChangeEnrollment,
    handleEcommerceCheckout,
  } = useEnrollmentActions({
    courseId,
    ecommerceCheckoutLink: courseAboutData?.ecommerceCheckoutLink,
  });

  const processedOverview = useMemo(
    () => processOverviewContent(courseAboutData?.overview || '', LMS_BASE_URL, {
      getFallbackStaffName: (index) => intl.formatMessage(messages.legacyStaffFallbackName, {
        index: index + 1,
      }),
    }),
    [courseAboutData?.overview, LMS_BASE_URL, intl],
  );

  const courseImage = resolveCourseImage(
    courseAboutData?.media?.image?.large
    || courseAboutData?.media?.image?.raw
    || courseAboutData?.media?.courseImage?.uri,
  );
  const [resolvedCourseImage, setResolvedCourseImage] = useState(courseImage);

  useEffect(() => {
    setResolvedCourseImage(courseImage);
  }, [courseImage]);

  const primaryAction = useMemo<PrimaryAction>(() => {
    if (!courseAboutData) {
      return {
        kind: 'none',
        status: intl.formatMessage(messages.actionUnavailableStatus),
        helper: intl.formatMessage(messages.actionUnavailableHelper),
      };
    }

    const learningHomeUrl = getLearningHomePageUrl(courseAboutData.id);

    if (authenticatedUser && courseAboutData.enrollment.isActive) {
      return {
        kind: 'link',
        label: intl.formatMessage(messages.actionAccessCourseLabel),
        href: learningHomeUrl,
        status: intl.formatMessage(messages.actionAccessCourseStatus),
        helper: intl.formatMessage(messages.actionAccessCourseHelper),
      };
    }

    if (courseAboutData.allowAnonymous && courseAboutData.showCoursewareLink) {
      return {
        kind: 'link',
        label: intl.formatMessage(messages.actionViewCourseLabel),
        href: learningHomeUrl,
        status: intl.formatMessage(messages.actionViewCourseStatus),
        helper: intl.formatMessage(messages.actionViewCourseHelper),
      };
    }

    if (courseAboutData.isCourseFull) {
      return {
        kind: 'none',
        status: intl.formatMessage(messages.actionCourseFullStatus),
        helper: intl.formatMessage(messages.actionCourseFullHelper),
      };
    }

    if (courseAboutData.invitationOnly && !courseAboutData.canEnroll) {
      return {
        kind: 'none',
        status: intl.formatMessage(messages.actionInvitationOnlyStatus),
        helper: intl.formatMessage(messages.actionInvitationOnlyHelper),
      };
    }

    if (courseAboutData.ecommerceCheckout) {
      return {
        kind: 'button',
        label: intl.formatMessage(messages.actionBuyAccessLabel),
        onClick: handleEcommerceCheckout,
        status: intl.formatMessage(messages.actionPaidStatus),
        helper: intl.formatMessage(messages.actionPaidHelper),
      };
    }

    if (!courseAboutData.isShibCourse && !courseAboutData.canEnroll) {
      return {
        kind: 'none',
        status: intl.formatMessage(messages.actionClosedStatus),
        helper: intl.formatMessage(messages.actionClosedHelper),
      };
    }

    return {
      kind: 'button',
      label: isEnrollmentPending
        ? intl.formatMessage(messages.actionEnrollPendingLabel)
        : intl.formatMessage(messages.actionEnrollLabel),
      onClick: handleChangeEnrollment,
      status: intl.formatMessage(messages.actionEnrollStatus),
      helper: intl.formatMessage(messages.actionEnrollHelper),
      disabled: isEnrollmentPending,
    };
  }, [
    authenticatedUser,
    courseAboutData,
    handleChangeEnrollment,
    handleEcommerceCheckout,
    isEnrollmentPending,
    intl,
  ]);

  const detailItems = useMemo<DetailItem[]>(() => {
    if (!courseAboutData) {
      return [];
    }

    return [
      {
        icon: faLayerGroup,
        label: intl.formatMessage(messages.detailReference),
        value: courseAboutData.displayNumberWithDefault || intl.formatMessage(messages.detailReferenceFallback),
      },
      {
        icon: faCalendarAlt,
        label: intl.formatMessage(messages.detailStart),
        value: courseAboutData.startDateIsStillDefault
          ? intl.formatMessage(messages.detailStartFallback)
          : getStartDateDisplay({
            advertisedStart: courseAboutData.advertisedStart,
            start: courseAboutData.start,
          }, intl),
      },
      courseAboutData.end ? {
        icon: faCalendarAlt,
        label: intl.formatMessage(messages.detailEnd),
        value: formatDate(courseAboutData.end, intl),
      } : null,
      courseAboutData.effort ? {
        icon: faClock,
        label: intl.formatMessage(messages.detailEffort),
        value: courseAboutData.effort,
      } : null,
      {
        icon: faGlobe,
        label: intl.formatMessage(messages.detailLanguage),
        value: getLanguageName(courseAboutData.language || 'fr', intl.locale),
      },
      {
        icon: faCheckCircle,
        label: intl.formatMessage(messages.detailAccess),
        value: getPriceLabel(intl, courseAboutData.coursePrice, courseAboutData.ecommerceCheckout),
      },
    ].filter((item): item is DetailItem => Boolean(item));
  }, [courseAboutData, intl]);

  const heroHighlights = useMemo(
    () => detailItems.slice(0, 4),
    [detailItems],
  );

  const requirementList = useMemo(
    () => normalizeTextList(courseAboutData?.requirements),
    [courseAboutData?.requirements],
  );

  const prerequisiteList = useMemo(() => {
    const directEntries = normalizeTextList(courseAboutData?.prerequisites);
    const relatedCourseEntries = (courseAboutData?.preRequisiteCourses || [])
      .map((course) => course.display || course.key)
      .filter(Boolean);

    return [...directEntries, ...relatedCourseEntries];
  }, [courseAboutData?.preRequisiteCourses, courseAboutData?.prerequisites]);

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !courseAboutData) {
    return (
      <>
        <Head title={intl.formatMessage(messages.unavailableTitle)} />
        <div className="catalog-course-about-page">
          <div className="catalog-course-about-shell">
            <AlertNotification
              variant="danger"
              title={intl.formatMessage(messages.unavailableCardTitle)}
              message={(
                <ErrorPage
                  message={intl.formatMessage(messages.errorMessage, {
                    supportEmail: INFO_EMAIL,
                  })}
                />
              )}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Head title={courseAboutData.name || ''} />
      <div className="catalog-course-about-page">
        <section className="catalog-course-about-hero">
          <div className="catalog-course-about-shell catalog-course-about-hero__shell">
            <Link className="catalog-course-about-hero__back-link" to={ROUTES.COURSES}>
              <FontAwesomeIcon icon={faArrowLeft} />
              {intl.formatMessage(messages.backToCatalog)}
            </Link>

            <div className="catalog-course-about-hero__layout">
              <div className="catalog-course-about-hero__copy">
                <span className="catalog-home-section__eyebrow">{intl.formatMessage(messages.heroEyebrow)}</span>

                <div className="catalog-course-about-hero__meta-row">
                  <span>{courseAboutData.displayOrgWithDefault}</span>
                  <span>{courseAboutData.displayNumberWithDefault}</span>
                </div>

                <h1 className="catalog-course-about-hero__title">{courseAboutData.name}</h1>
                <p className="catalog-course-about-hero__description">{courseAboutData.shortDescription}</p>

                <div className="catalog-course-about-hero__chips">
                  <span>{getPacingLabel(intl, courseAboutData.pacing)}</span>
                  <span>{getLanguageName(courseAboutData.language || 'fr', intl.locale)}</span>
                  <span>{getPriceLabel(intl, courseAboutData.coursePrice, courseAboutData.ecommerceCheckout)}</span>
                </div>

                <div className="catalog-course-about-hero__actions">
                  {renderPrimaryAction(primaryAction)}
                  <Link className="catalog-ui-button catalog-ui-button--secondary" to={ROUTES.COURSES}>
                    {intl.formatMessage(messages.exploreOtherCourses)}
                  </Link>
                </div>

                <div className="catalog-course-about-hero__status">
                  <span className="catalog-course-about-status-pill">
                    <FontAwesomeIcon
                      icon={
                        primaryAction.kind === 'none'
                          ? faLock
                          : primaryAction.kind === 'link'
                            ? faCheckCircle
                            : faInfoCircle
                      }
                    />
                    {primaryAction.status}
                  </span>
                  <p>{primaryAction.helper}</p>
                </div>

                {enrollmentError ? (
                  <div className="catalog-course-about-hero__alert">
                    <AlertNotification
                      variant="danger"
                      title={intl.formatMessage(messages.heroErrorTitle)}
                      message={enrollmentError}
                    />
                  </div>
                ) : null}
              </div>

              <div className="catalog-course-about-media">
                <div className="catalog-course-about-media__frame">
                  <img
                    src={resolvedCourseImage}
                    alt={courseAboutData.name}
                    className="catalog-course-about-media__image"
                    onError={() => {
                      if (resolvedCourseImage !== noCourseImg) {
                        setResolvedCourseImage(noCourseImg);
                      }
                    }}
                  />
                  {courseAboutData.media?.courseVideo?.uri ? (
                    <a
                      className="catalog-course-about-media__video"
                      href={courseAboutData.media.courseVideo.uri}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FontAwesomeIcon icon={faPlayCircle} />
                      {intl.formatMessage(messages.watchPresentation)}
                    </a>
                  ) : null}
                </div>
              </div>
            </div>

            {heroHighlights.length ? (
              <div className="catalog-course-about-highlights">
                {heroHighlights.map((item) => (
                  <article key={`${item.label}-${item.value}`} className="catalog-course-about-highlight">
                    <span className="catalog-course-about-highlight__icon">
                      <FontAwesomeIcon icon={item.icon} />
                    </span>
                    <span className="catalog-course-about-highlight__label">{item.label}</span>
                    <strong className="catalog-course-about-highlight__value">{item.value}</strong>
                  </article>
                ))}
              </div>
            ) : null}
          </div>
        </section>

        <section className="catalog-course-about-content">
          <div className="catalog-course-about-shell catalog-course-about-content__shell">
            <div className="catalog-course-about-main">
              <article className="catalog-course-about-card catalog-course-about-overview">
                <div className="catalog-course-about-card__header">
                  <span className="catalog-home-section__eyebrow">{intl.formatMessage(messages.overviewEyebrow)}</span>
                  <h2>{intl.formatMessage(messages.overviewTitle)}</h2>
                </div>
                {processedOverview ? (
                  <div
                    className="catalog-course-about-overview__content"
                    dangerouslySetInnerHTML={{ __html: processedOverview }}
                  />
                ) : (
                  <p className="catalog-course-about-overview__empty">
                    {intl.formatMessage(messages.overviewEmpty)}
                  </p>
                )}
              </article>

              {requirementList.length ? (
                <section className="catalog-course-about-card catalog-course-about-list-card">
                  <div className="catalog-course-about-card__header">
                    <span className="catalog-home-section__eyebrow">{intl.formatMessage(messages.requirementsEyebrow)}</span>
                    <h2>{intl.formatMessage(messages.requirementsTitle)}</h2>
                  </div>
                  <ul className="catalog-course-about-bullet-list">
                    {requirementList.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {prerequisiteList.length ? (
                <section className="catalog-course-about-card catalog-course-about-list-card">
                  <div className="catalog-course-about-card__header">
                    <span className="catalog-home-section__eyebrow">{intl.formatMessage(messages.prerequisitesEyebrow)}</span>
                    <h2>{intl.formatMessage(messages.prerequisitesTitle)}</h2>
                  </div>
                  <ul className="catalog-course-about-bullet-list">
                    {prerequisiteList.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </div>

            <aside className="catalog-course-about-sidebar">
              <div className="catalog-course-about-card catalog-course-about-sidebar__card catalog-course-about-sidebar__decision">
                <div className="catalog-course-about-card__header">
                  <span className="catalog-home-section__eyebrow">{intl.formatMessage(messages.decisionEyebrow)}</span>
                  <h2>{intl.formatMessage(messages.decisionTitle)}</h2>
                </div>
                <span className="catalog-course-about-status-pill">
                  <FontAwesomeIcon
                    icon={
                      primaryAction.kind === 'none'
                        ? faLock
                        : primaryAction.kind === 'link'
                          ? faCheckCircle
                          : faInfoCircle
                    }
                  />
                  {primaryAction.status}
                </span>
                <p className="catalog-course-about-sidebar__summary">
                  {primaryAction.helper}
                </p>
                {renderPrimaryAction(primaryAction)}
                {primaryAction.kind === 'none' ? (
                  <Link className="catalog-ui-button catalog-ui-button--secondary" to={ROUTES.COURSES}>
                    {intl.formatMessage(messages.otherSessions)}
                  </Link>
                ) : null}
              </div>

              <div className="catalog-course-about-card catalog-course-about-sidebar__card">
                <div className="catalog-course-about-card__header">
                  <span className="catalog-home-section__eyebrow">{intl.formatMessage(messages.keyInfoEyebrow)}</span>
                  <h2>{intl.formatMessage(messages.keyInfoTitle)}</h2>
                </div>

                <div className="catalog-course-about-details">
                  {detailItems.map((item) => (
                    <div key={`${item.label}-${item.value}`} className="catalog-course-about-detail">
                      <span className="catalog-course-about-detail__icon">
                        <FontAwesomeIcon icon={item.icon} />
                      </span>
                      <div>
                        <span className="catalog-course-about-detail__label">{item.label}</span>
                        <strong className="catalog-course-about-detail__value">{item.value}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="catalog-course-about-card catalog-course-about-sidebar__card">
                <div className="catalog-course-about-card__header">
                  <span className="catalog-home-section__eyebrow">{intl.formatMessage(messages.supportEyebrow)}</span>
                  <h2>{intl.formatMessage(messages.supportTitle)}</h2>
                </div>
                <p className="catalog-course-about-sidebar__summary">
                  {intl.formatMessage(messages.supportSummary)}
                </p>
                <a className="catalog-ui-button catalog-ui-button--secondary" href={`mailto:${INFO_EMAIL}`}>
                  {intl.formatMessage(messages.supportAction)}
                </a>
              </div>
            </aside>
          </div>
        </section>
      </div>
    </>
  );
};

export default CourseAboutPage;
